import { useToast } from '@lego/klik-ui';
import { useEffect, useState } from 'react';
import { ToastOptions } from '@/utility/ToastOptions';

const FEEDBACK_MS = 2000;

/**
 * Copy-to-clipboard with transient `copied` / `error` flags for button and tooltip feedback. Both
 * flags auto-reset, and their timers are cleared on unmount so a component that disappears within
 * the feedback window can't set state after teardown.
 */
export const useCopyToClipboard = () => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(false), FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [error]);

  const copy = async (value: string) => {
    // Reset both flags: a retry while `copied` is still showing would otherwise leave a stale success
    // alongside a new failure, and re-copying identical text wouldn't restart the feedback timer.
    setCopied(false);
    setError(false);

    if (!navigator.clipboard) {
      setError(true);
      toast(ToastOptions.error('Copy failed', 'Clipboard API not available'));
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setError(true);
      toast(ToastOptions.error('Copy failed', 'Unable to copy to clipboard'));
    }
  };

  return { copied, error, copy };
};
